import React, { useState, useEffect } from 'react';
import { apiGet, apiPost, apiPut } from '../utils/api';
import FilePickerModal from './FilePickerModal';
import { datetimeLocalToUTC, utcToDatetimeLocal } from '../utils/timezone';

interface Club {
  id: string;
  name: string;
  address?: string;
  rates?: string;
}

interface RegistrationDigestTemplate {
  id: string;
  templateCode: string;
  name: string;
  languageMode: string;
  isDefault: boolean;
}

interface DigestTelegramTarget {
  id: string;
  name: string;
  telegramChatId: string;
  telegramTopicId: string;
  isActive: boolean;
}

type ResultsTelegramTargetPayload = {
  chatId: string;
  topicId: string | null;
};

interface TournamentFormProps {
  tournament?: {
    id: string;
    name: string;
    description: string;
    startDate: string;
    endDate?: string;
    season?: string;
    registrationStartAt?: string;
    registrationEndAt?: string;
    status: string;
    budget?: number;
    baseFee?: number;
    clubId?: string;
    courtRentalPrice?: number;
    format?: string;
    levelBrackets?: string;
    hideResults?: boolean;
    showCourts?: boolean;
    excludeFromWebsite?: boolean;
    disableFeeAccrual?: boolean;
    inviteOnlyRegistration?: boolean;
    digestEnabled?: boolean;
    digestSendTime?: string;
    digestTimeZone?: string;
    digestTelegramChatId?: string;
    digestTelegramTopicId?: string;
    resultsTelegramEnabled?: boolean;
    resultsTelegramChatId?: string;
    resultsTelegramTopicId?: string;
    resultsTelegramTargetsJson?: string;
    scheduleTelegramEnabled?: boolean;
    scheduleTelegramChatId?: string;
    scheduleTelegramTopicId?: string;
    digestParticipantsListLink?: string;
    digestRegistrationLink?: string;
    digestLanguageMode?: 'ro' | 'ru' | 'en' | 'ro_ru' | 'ru_ro';
    digestTemplateId?: string;
    rulesRu?: string;
    rulesRo?: string;
    rulesEn?: string;
    rulesIsPublished?: boolean;
    rulesUpdatedAt?: string;
    tournamentClubs?: Array<{
      id: string;
      clubId: string;
      isPrimary: boolean;
      courtRentalPrice?: number;
      surfaceType?: string;
      club: {
        id: string;
        name: string;
      };
    }>;
  };
  onSave: (data: any) => void;
  onCancel: () => void;
}

const TournamentForm: React.FC<TournamentFormProps> = ({
  tournament,
  onSave,
  onCancel
}) => {
  const DEFAULT_BRACKET_FORMAT_KEY = '__defaultBracketFormat';
  const SEASON_OPTIONS = [
    '2026-2027',
    '2026',
    '2025-2026',
    '2025',
    '2024-2025',
    '2024',
    '2023-2024',
    '2023',
    '2022-2023',
    '2022'
  ];

  // Инициализируем цены по клубам из существующего турнира
  const initialClubPrices: Record<string, number | ''> = {};
  const initialClubSurfaces: Record<string, string> = {};
  if (tournament?.tournamentClubs) {
    tournament.tournamentClubs.forEach(tc => {
      if (tc.courtRentalPrice !== null && tc.courtRentalPrice !== undefined) {
        initialClubPrices[tc.clubId] = tc.courtRentalPrice;
      }
      if (tc.surfaceType) {
        initialClubSurfaces[tc.clubId] = tc.surfaceType;
      }
    });
  }

  // Парсим logos из JSON строки
  const parseLogos = (logosJson?: string | null): string[] => {
    if (!logosJson) return [];
    try {
      return JSON.parse(logosJson);
    } catch {
      return [];
    }
  };

  const parseResultsTelegramTargets = (rawJson?: string | null): ResultsTelegramTargetPayload[] => {
    if (!rawJson) return [];
    try {
      const parsed = JSON.parse(rawJson);
      if (!Array.isArray(parsed)) return [];
      const targets: ResultsTelegramTargetPayload[] = [];
      parsed.forEach((item: any) => {
        const chatId = String(item?.chatId || '').trim();
        const topicId = String(item?.topicId || '').trim();
        if (!chatId) return;
        targets.push({
          chatId,
          topicId: topicId || null
        });
      });
      return targets;
    } catch {
      return [];
    }
  };

  const TOURNAMENT_LEVELS = [
    'S-4F',
    'S-4M',
    'S-5',
    'S-6',
    'S-7',
    'S-8',
    'S-9',
    'D-Mixt',
    'D-6',
    'D-6.5',
    'D-7',
    'D-ProAm'
  ];

  const SURFACE_OPTIONS = [
    { value: 'hard', label: 'Хард' },
    { value: 'clay', label: 'Грунт' },
    { value: 'grass', label: 'Трава' },
    { value: 'synthetic', label: 'Синтетика' }
  ];

  type RegistrationLevelConfig = {
    enabled: boolean;
    maxPlayers: number | '';
  };

  const parseLevelBrackets = (value?: string | null): Record<string, any> => {
    if (!value) return {};
    try {
      const parsed = JSON.parse(value);
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  };

  const parseRegistrationLevelConfigs = (value?: string | null): Record<string, RegistrationLevelConfig> => {
    const parsedBrackets = parseLevelBrackets(value);
    const configs: Record<string, RegistrationLevelConfig> = {};

    TOURNAMENT_LEVELS.forEach((level) => {
      const data = parsedBrackets[level];
      if (data && typeof data === 'object') {
        const enabled = data.registrationEnabled === true;
        const maxPlayersRaw = data.registrationMaxPlayers;
        const maxPlayers = typeof maxPlayersRaw === 'number' && Number.isFinite(maxPlayersRaw)
          ? maxPlayersRaw
          : '';
        configs[level] = { enabled, maxPlayers };
      } else {
        configs[level] = { enabled: false, maxPlayers: '' };
      }
    });

    return configs;
  };

  const resolveDefaultBracketFormat = (value?: string | null): string => {
    const brackets = parseLevelBrackets(value);
    const raw = brackets[DEFAULT_BRACKET_FORMAT_KEY];
    if (typeof raw === 'string') return raw;
    if (raw && typeof raw === 'object') {
      const fromFormat = String((raw as any).format || '').trim();
      if (fromFormat) return fromFormat;
      const fromTemplate = String((raw as any).templateId || '').trim();
      if (fromTemplate) return fromTemplate;
    }
    return '';
  };

  const toDateTimeInputValue = (value?: string): string => {
    if (!value) return '';
    return utcToDatetimeLocal(value);
  };

  const toApiDateTimeValue = (value?: string | null): string | null => {
    if (!value) return null;
    return datetimeLocalToUTC(value) || null;
  };

  const [formData, setFormData] = useState({
    name: tournament?.name || '',
    description: tournament?.description || '',
    startDate: tournament?.startDate ? tournament.startDate.split('T')[0] : '',
    endDate: tournament?.endDate ? tournament.endDate.split('T')[0] : '',
    season: (tournament as any)?.season || '',
    registrationStartAt: toDateTimeInputValue((tournament as any)?.registrationStartAt),
    registrationEndAt: toDateTimeInputValue((tournament as any)?.registrationEndAt),
    status: tournament?.status || 'PLANNED',
    budget: tournament?.budget || '',
    baseFee: (tournament as any)?.baseFee ?? '',
    clubId: tournament?.clubId || '',
    color: (tournament as any)?.color || '#FDE7C8',
    format: tournament?.format || 'SINGLES',
    hideResults: tournament?.hideResults || false,
    showCourts: tournament?.showCourts || false,
    excludeFromWebsite: (tournament as any)?.excludeFromWebsite || false,
    disableFeeAccrual: (tournament as any)?.disableFeeAccrual || false,
    inviteOnlyRegistration: (tournament as any)?.inviteOnlyRegistration || false,
    digestEnabled: (tournament as any)?.digestEnabled || false,
    digestSendTime: (tournament as any)?.digestSendTime || '20:00',
    digestTimeZone: (tournament as any)?.digestTimeZone || 'Europe/Chisinau',
    digestTelegramChatId: (tournament as any)?.digestTelegramChatId || '',
    digestTelegramTopicId: (tournament as any)?.digestTelegramTopicId || '',
    resultsTelegramEnabled: (tournament as any)?.resultsTelegramEnabled || false,
    resultsTelegramChatId: (tournament as any)?.resultsTelegramChatId || '',
    resultsTelegramTopicId: (tournament as any)?.resultsTelegramTopicId || '',
    resultsTelegramTargetsJson: (tournament as any)?.resultsTelegramTargetsJson || '',
    scheduleTelegramEnabled: (tournament as any)?.scheduleTelegramEnabled || false,
    scheduleTelegramChatId: (tournament as any)?.scheduleTelegramChatId || '',
    scheduleTelegramTopicId: (tournament as any)?.scheduleTelegramTopicId || '',
    digestParticipantsListLink: (tournament as any)?.digestParticipantsListLink || '',
    digestRegistrationLink: (tournament as any)?.digestRegistrationLink || '',
    digestLanguageMode: (tournament as any)?.digestLanguageMode || 'ro_ru',
    digestTemplateId: (tournament as any)?.digestTemplateId || '',
    defaultBracketFormat: resolveDefaultBracketFormat(tournament?.levelBrackets),
    rulesRu: (tournament as any)?.rulesRu || '',
    rulesRo: (tournament as any)?.rulesRo || '',
    rulesEn: (tournament as any)?.rulesEn || '',
    rulesIsPublished: (tournament as any)?.rulesIsPublished || false,
    rulesUpdatedAt: (tournament as any)?.rulesUpdatedAt || null,
    clubIds: tournament?.tournamentClubs?.map(tc => tc.clubId) || [],
    clubPrices: initialClubPrices,
    clubSurfaces: initialClubSurfaces,
    logos: parseLogos((tournament as any)?.logos) || [],
    levelBracketsRaw: parseLevelBrackets(tournament?.levelBrackets),
    registrationLevels: parseRegistrationLevelConfigs(tournament?.levelBrackets)
  });

  const [clubs, setClubs] = useState<Club[]>([]);
  const [digestTemplates, setDigestTemplates] = useState<RegistrationDigestTemplate[]>([]);
  const [digestTelegramTargets, setDigestTelegramTargets] = useState<DigestTelegramTarget[]>([]);
  const [selectedDigestTargetId, setSelectedDigestTargetId] = useState<string>('');
  const [selectedResultsTargetIds, setSelectedResultsTargetIds] = useState<string[]>([]);
  const [selectedScheduleTargetId, setSelectedScheduleTargetId] = useState<string>('');
  const [digestTargetModalOpen, setDigestTargetModalOpen] = useState(false);
  const [digestTargetSaving, setDigestTargetSaving] = useState(false);
  const [editingDigestTargetId, setEditingDigestTargetId] = useState<string | null>(null);
  const [digestTargetForm, setDigestTargetForm] = useState({
    name: '',
    telegramChatId: '',
    telegramTopicId: ''
  });
  const [loading, setLoading] = useState(false);
  const [digestSendingMode, setDigestSendingMode] = useState<'initial' | 'current' | 'final' | null>(null);
  const [manualDigestTemplates, setManualDigestTemplates] = useState<{
    initial: string;
    current: string;
    final: string;
  }>({
    initial: '',
    current: '',
    final: ''
  });
  const [error, setError] = useState<string | null>(null);
  const [filePickerOpen, setFilePickerOpen] = useState(false);
  const [filePickerIndex, setFilePickerIndex] = useState<number | null>(null);

  // Загрузка клубов
  useEffect(() => {
    const fetchClubs = async () => {
      try {
        const data = await apiGet<any>('/clubs', { isActive: true });
        setClubs(data.clubs || []);
      } catch (error) {
        console.error('Error fetching clubs:', error);
      }
    };

    fetchClubs();
  }, []);

  // Обновление формы при изменении турнира
  useEffect(() => {
    if (tournament) {
      const clubPrices: Record<string, number | ''> = {};
      const clubSurfaces: Record<string, string> = {};
      if (tournament.tournamentClubs) {
        tournament.tournamentClubs.forEach(tc => {
          if (tc.courtRentalPrice !== null && tc.courtRentalPrice !== undefined) {
            clubPrices[tc.clubId] = tc.courtRentalPrice;
          }
          if (tc.surfaceType) {
            clubSurfaces[tc.clubId] = tc.surfaceType;
          }
        });
      }

      setFormData({
        name: tournament.name || '',
        description: tournament.description || '',
        startDate: tournament.startDate ? tournament.startDate.split('T')[0] : '',
        endDate: tournament.endDate ? tournament.endDate.split('T')[0] : '',
        season: (tournament as any)?.season || '',
        registrationStartAt: toDateTimeInputValue((tournament as any)?.registrationStartAt),
        registrationEndAt: toDateTimeInputValue((tournament as any)?.registrationEndAt),
        status: tournament.status || 'PLANNED',
        budget: tournament.budget || '',
        baseFee: (tournament as any)?.baseFee ?? '',
        clubId: tournament.clubId || '',
        color: (tournament as any)?.color || '#FDE7C8',
        format: tournament.format || 'SINGLES',
        hideResults: tournament.hideResults || false,
        showCourts: tournament.showCourts || false,
        excludeFromWebsite: (tournament as any).excludeFromWebsite || false,
        disableFeeAccrual: (tournament as any).disableFeeAccrual || false,
        inviteOnlyRegistration: (tournament as any).inviteOnlyRegistration || false,
        digestEnabled: (tournament as any)?.digestEnabled || false,
        digestSendTime: (tournament as any)?.digestSendTime || '20:00',
        digestTimeZone: (tournament as any)?.digestTimeZone || 'Europe/Chisinau',
        digestTelegramChatId: (tournament as any)?.digestTelegramChatId || '',
        digestTelegramTopicId: (tournament as any)?.digestTelegramTopicId || '',
        resultsTelegramEnabled: (tournament as any)?.resultsTelegramEnabled || false,
        resultsTelegramChatId: (tournament as any)?.resultsTelegramChatId || '',
        resultsTelegramTopicId: (tournament as any)?.resultsTelegramTopicId || '',
        resultsTelegramTargetsJson: (tournament as any)?.resultsTelegramTargetsJson || '',
        scheduleTelegramEnabled: (tournament as any)?.scheduleTelegramEnabled || false,
        scheduleTelegramChatId: (tournament as any)?.scheduleTelegramChatId || '',
        scheduleTelegramTopicId: (tournament as any)?.scheduleTelegramTopicId || '',
        digestParticipantsListLink: (tournament as any)?.digestParticipantsListLink || '',
        digestRegistrationLink: (tournament as any)?.digestRegistrationLink || '',
        digestLanguageMode: (tournament as any)?.digestLanguageMode || 'ro_ru',
        digestTemplateId: (tournament as any)?.digestTemplateId || '',
        defaultBracketFormat: resolveDefaultBracketFormat(tournament?.levelBrackets),
        rulesRu: (tournament as any)?.rulesRu || '',
        rulesRo: (tournament as any)?.rulesRo || '',
        rulesEn: (tournament as any)?.rulesEn || '',
        rulesIsPublished: (tournament as any)?.rulesIsPublished || false,
        rulesUpdatedAt: (tournament as any)?.rulesUpdatedAt || null,
        clubIds: tournament.tournamentClubs?.map(tc => tc.clubId) || [],
        clubPrices: clubPrices,
        clubSurfaces: clubSurfaces,
        logos: parseLogos((tournament as any)?.logos) || [],
        levelBracketsRaw: parseLevelBrackets(tournament.levelBrackets),
        registrationLevels: parseRegistrationLevelConfigs(tournament.levelBrackets)
      });
    }
  }, [tournament]);

  useEffect(() => {
    const fetchDigestTemplates = async () => {
      try {
        const data = await apiGet<{ templates?: RegistrationDigestTemplate[] }>('/registration-digest/templates');
        setDigestTemplates(Array.isArray(data?.templates) ? data.templates : []);
      } catch (err) {
        console.error('Error fetching digest templates:', err);
        setDigestTemplates([]);
      }
    };

    fetchDigestTemplates();
  }, []);

  useEffect(() => {
    const fetchDigestTargets = async () => {
      try {
        const data = await apiGet<{ targets?: DigestTelegramTarget[] }>('/digest-telegram-targets');
        setDigestTelegramTargets(Array.isArray(data?.targets) ? data.targets : []);
      } catch (err) {
        console.error('Error fetching digest telegram targets:', err);
        setDigestTelegramTargets([]);
      }
    };
    fetchDigestTargets();
  }, []);

  useEffect(() => {
    const chatId = ((formData as any).digestTelegramChatId || '').toString().trim();
    const topicId = ((formData as any).digestTelegramTopicId || '').toString().trim();
    if (!chatId) {
      setSelectedDigestTargetId('');
      return;
    }
    const found = digestTelegramTargets.find(
      (target) =>
        target.telegramChatId === chatId &&
        String(target.telegramTopicId || '').trim() === topicId
    );
    setSelectedDigestTargetId(found?.id || '');
  }, [digestTelegramTargets, (formData as any).digestTelegramChatId, (formData as any).digestTelegramTopicId]);

  useEffect(() => {
    const parsedTargets = parseResultsTelegramTargets((formData as any).resultsTelegramTargetsJson);
    const normalizedFromJson = parsedTargets
      .map((target) =>
        digestTelegramTargets.find(
          (item) =>
            item.telegramChatId === target.chatId &&
            String(item.telegramTopicId || '').trim() === String(target.topicId || '').trim()
        )?.id || ''
      )
      .filter(Boolean);

    if (normalizedFromJson.length > 0) {
      setSelectedResultsTargetIds(Array.from(new Set(normalizedFromJson)));
      return;
    }

    const chatId = ((formData as any).resultsTelegramChatId || '').toString().trim();
    const topicId = ((formData as any).resultsTelegramTopicId || '').toString().trim();
    if (!chatId) {
      setSelectedResultsTargetIds([]);
      return;
    }
    const found = digestTelegramTargets.find(
      (target) => target.telegramChatId === chatId && String(target.telegramTopicId || '').trim() === topicId
    );
    setSelectedResultsTargetIds(found?.id ? [found.id] : []);
  }, [
    digestTelegramTargets,
    (formData as any).resultsTelegramChatId,
    (formData as any).resultsTelegramTopicId,
    (formData as any).resultsTelegramTargetsJson
  ]);

  useEffect(() => {
    const chatId = ((formData as any).scheduleTelegramChatId || '').toString().trim();
    const topicId = ((formData as any).scheduleTelegramTopicId || '').toString().trim();
    if (!chatId) {
      setSelectedScheduleTargetId('');
      return;
    }
    const found = digestTelegramTargets.find(
      (target) =>
        target.telegramChatId === chatId &&
        String(target.telegramTopicId || '').trim() === topicId
    );
    setSelectedScheduleTargetId(found?.id || '');
  }, [digestTelegramTargets, (formData as any).scheduleTelegramChatId, (formData as any).scheduleTelegramTopicId]);

  useEffect(() => {
    const selected = ((formData as any).digestTemplateId || '').toString();
    setManualDigestTemplates({
      initial: selected,
      current: selected,
      final: selected
    });
  }, [tournament?.id]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if ((formData as any).registrationStartAt && (formData as any).registrationEndAt) {
        const regStart = new Date((formData as any).registrationStartAt);
        const regEnd = new Date((formData as any).registrationEndAt);
        if (!Number.isNaN(regStart.getTime()) && !Number.isNaN(regEnd.getTime()) && regStart > regEnd) {
          setError('Период регистрации задан неверно: "с" позже, чем "по".');
          setLoading(false);
          return;
        }
      }

      // Преобразуем clubPrices в числовые значения, исключая пустые
      const clubPrices: Record<string, number> = {};
      Object.entries(formData.clubPrices).forEach(([clubId, price]) => {
        if (price !== '' && formData.clubIds.includes(clubId)) {
          clubPrices[clubId] = Number(price);
        }
      });
      const clubSurfaces: Record<string, string> = {};
      Object.entries((formData as any).clubSurfaces || {}).forEach(([clubId, surface]) => {
        const normalized = String(surface || '').trim();
        if (normalized && formData.clubIds.includes(clubId)) {
          clubSurfaces[clubId] = normalized;
        }
      });

      const mergedLevelBrackets: Record<string, any> = {
        ...(formData as any).levelBracketsRaw
      };

      TOURNAMENT_LEVELS.forEach((level) => {
        const cfg = (formData as any).registrationLevels?.[level] as RegistrationLevelConfig | undefined;
        const current = mergedLevelBrackets[level];
        const currentObj =
          current && typeof current === 'object'
            ? { ...current }
            : typeof current === 'string'
              ? { templateId: current }
              : {};

        if (cfg?.enabled) {
          const normalizedMaxPlayers =
            cfg.maxPlayers === '' || cfg.maxPlayers === null || cfg.maxPlayers === undefined
              ? null
              : Number(cfg.maxPlayers);

          currentObj.registrationEnabled = true;
          currentObj.registrationMaxPlayers =
            normalizedMaxPlayers && Number.isFinite(normalizedMaxPlayers) && normalizedMaxPlayers > 0
              ? Math.floor(normalizedMaxPlayers)
              : null;

          mergedLevelBrackets[level] = currentObj;
        } else if (currentObj && typeof currentObj === 'object') {
          delete currentObj.registrationEnabled;
          delete currentObj.registrationMaxPlayers;

          if (Object.keys(currentObj).length > 0) {
            mergedLevelBrackets[level] = currentObj;
          } else {
            delete mergedLevelBrackets[level];
          }
        }
      });

      const defaultBracketFormat = ((formData as any).defaultBracketFormat || '').toString().trim();
      if (defaultBracketFormat) {
        mergedLevelBrackets[DEFAULT_BRACKET_FORMAT_KEY] = defaultBracketFormat;
      } else {
        delete mergedLevelBrackets[DEFAULT_BRACKET_FORMAT_KEY];
      }

      const levelBracketsJson =
        Object.keys(mergedLevelBrackets).length > 0
          ? JSON.stringify(mergedLevelBrackets)
          : null;

      const selectedResultsTargets = selectedResultsTargetIds
        .map((targetId) => digestTelegramTargets.find((target) => target.id === targetId))
        .filter((target): target is DigestTelegramTarget => Boolean(target))
        .map((target) => ({
          chatId: String(target.telegramChatId || '').trim(),
          topicId: String(target.telegramTopicId || '').trim() || null
        }))
        .filter((target) => target.chatId);
      const fallbackResultsChatId = String((formData as any).resultsTelegramChatId || '').trim();
      const fallbackResultsTopicId = String((formData as any).resultsTelegramTopicId || '').trim();
      const resultsTargetsPayload =
        selectedResultsTargets.length > 0
          ? selectedResultsTargets
          : fallbackResultsChatId
            ? [{ chatId: fallbackResultsChatId, topicId: fallbackResultsTopicId || null }]
            : [];
      const primaryResultsTarget = resultsTargetsPayload[0] || null;

      const submitData = {
        ...formData,
        budget: formData.budget ? Number(formData.budget) : null,
        baseFee: (formData as any).baseFee !== '' && (formData as any).baseFee !== null && (formData as any).baseFee !== undefined
          ? Number((formData as any).baseFee)
          : null,
        endDate: formData.endDate || null,
        season: (formData as any).season || null,
        registrationStartAt: toApiDateTimeValue((formData as any).registrationStartAt),
        registrationEndAt: toApiDateTimeValue((formData as any).registrationEndAt),
        clubId: formData.clubId || null,
        color: formData.color,
        format: formData.format,
        hideResults: (formData as any).hideResults || false,
        showCourts: (formData as any).showCourts || false,
        excludeFromWebsite: (formData as any).excludeFromWebsite || false,
        disableFeeAccrual: (formData as any).disableFeeAccrual || false,
        inviteOnlyRegistration: (formData as any).inviteOnlyRegistration || false,
        digestEnabled: (formData as any).digestEnabled || false,
        digestSendTime: (formData as any).digestSendTime?.trim() || null,
        digestTimeZone: (formData as any).digestTimeZone?.trim() || 'Europe/Chisinau',
        digestTelegramChatId: (formData as any).digestTelegramChatId?.trim() || null,
        digestTelegramTopicId: (formData as any).digestTelegramTopicId?.trim() || null,
        resultsTelegramEnabled: (formData as any).resultsTelegramEnabled || false,
        resultsTelegramChatId: primaryResultsTarget?.chatId || null,
        resultsTelegramTopicId: primaryResultsTarget?.topicId || null,
        resultsTelegramTargetsJson: resultsTargetsPayload.length > 0 ? JSON.stringify(resultsTargetsPayload) : null,
        scheduleTelegramEnabled: (formData as any).scheduleTelegramEnabled || false,
        scheduleTelegramChatId: (formData as any).scheduleTelegramChatId?.trim() || null,
        scheduleTelegramTopicId: (formData as any).scheduleTelegramTopicId?.trim() || null,
        digestParticipantsListLink: (formData as any).digestParticipantsListLink?.trim() || null,
        digestRegistrationLink: (formData as any).digestRegistrationLink?.trim() || null,
        digestLanguageMode: (formData as any).digestLanguageMode || 'ro_ru',
        digestTemplateId: (formData as any).digestTemplateId || null,
        rulesRu: ((formData as any).rulesRu ?? '').toString(),
        rulesRo: ((formData as any).rulesRo ?? '').toString(),
        rulesEn: ((formData as any).rulesEn ?? '').toString(),
        rulesIsPublished: (formData as any).rulesIsPublished || false,
        clubIds: formData.clubIds,
        clubPrices: clubPrices,
        clubSurfaces,
        levelBrackets: levelBracketsJson,
        logos: (formData as any).logos && (formData as any).logos.length > 0 
          ? JSON.stringify((formData as any).logos.filter((logo: string) => logo.trim())) 
          : null
      };

      // Передаем данные в родительский компонент для обработки
      onSave(submitData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка сохранения');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleClubToggle = (clubId: string) => {
    setFormData(prev => {
      const newClubIds = prev.clubIds.includes(clubId)
        ? prev.clubIds.filter(id => id !== clubId)
        : [...prev.clubIds, clubId];
      
      // При снятии галочки удаляем цену из clubPrices
      const newClubPrices = { ...prev.clubPrices };
      const newClubSurfaces = { ...(prev as any).clubSurfaces };
      if (!newClubIds.includes(clubId)) {
        delete newClubPrices[clubId];
        delete newClubSurfaces[clubId];
      }

      return {
        ...prev,
        clubIds: newClubIds,
        clubPrices: newClubPrices,
        clubSurfaces: newClubSurfaces
      };
    });
  };

  const handleClubPriceChange = (clubId: string, price: string) => {
    setFormData(prev => ({
      ...prev,
      clubPrices: {
        ...prev.clubPrices,
        [clubId]: price === '' ? '' : Number.isFinite(parseFloat(price)) ? parseFloat(price) : ''
      }
    }));
  };

  const handleClubSurfaceChange = (clubId: string, surfaceType: string) => {
    setFormData((prev: any) => ({
      ...prev,
      clubSurfaces: {
        ...(prev.clubSurfaces || {}),
        [clubId]: surfaceType
      }
    }));
  };

  const handleRegistrationLevelToggle = (level: string) => {
    setFormData((prev: any) => ({
      ...prev,
      registrationLevels: {
        ...prev.registrationLevels,
        [level]: {
          ...prev.registrationLevels[level],
          enabled: !prev.registrationLevels[level]?.enabled
        }
      }
    }));
  };

  const handleRegistrationLevelMaxPlayersChange = (level: string, value: string) => {
    const parsed = value === '' ? '' : Math.max(0, Math.floor(Number(value) || 0));
    setFormData((prev: any) => ({
      ...prev,
      registrationLevels: {
        ...prev.registrationLevels,
        [level]: {
          ...prev.registrationLevels[level],
          maxPlayers: parsed
        }
      }
    }));
  };

  const handleManualDigestTemplateChange = (mode: 'initial' | 'current' | 'final', value: string) => {
    setManualDigestTemplates((prev) => ({
      ...prev,
      [mode]: value
    }));
    if (mode === 'current') {
      setFormData((prev: any) => ({
        ...prev,
        digestTemplateId: value
      }));
    }
  };

  const handleSendManualDigest = async (mode: 'initial' | 'current' | 'final') => {
    if (!tournament?.id) {
      alert('Сначала сохраните турнир, затем отправьте digest.');
      return;
    }

    try {
      setDigestSendingMode(mode);
      const templateId = manualDigestTemplates[mode] || null;
      const result = await apiPost<any>(`/registration-digest/tournaments/${tournament.id}/send-variant`, {
        mode,
        templateId,
        forceSend: mode === 'current'
      });

      if (result?.status === 'sent') {
        alert('Digest отправлен');
      } else if (result?.status === 'skipped') {
        alert(`Digest пропущен: ${result?.reason || 'skipped'}`);
      } else {
        alert(`Digest: ${result?.status || 'unknown'}`);
      }
    } catch (err: any) {
      console.error('Error sending digest variant:', err);
      alert(`Ошибка отправки digest: ${err?.message || 'Неизвестная ошибка'}`);
    } finally {
      setDigestSendingMode(null);
    }
  };

  const handleDigestTargetSelect = (value: string) => {
    if (value === '__add__') {
      setEditingDigestTargetId(null);
      setDigestTargetForm({ name: '', telegramChatId: '', telegramTopicId: '' });
      setDigestTargetModalOpen(true);
      return;
    }
    setSelectedDigestTargetId(value);
    const selected = digestTelegramTargets.find((target) => target.id === value);
    setFormData((prev: any) => ({
      ...prev,
      digestTelegramChatId: selected?.telegramChatId || '',
      digestTelegramTopicId: selected?.telegramTopicId || ''
    }));
  };

  const handleResultsTargetToggle = (targetId: string, checked: boolean) => {
    setSelectedResultsTargetIds((prev) => {
      const next = checked ? [...prev, targetId] : prev.filter((id) => id !== targetId);
      return Array.from(new Set(next));
    });
  };

  const handleAddResultsTarget = () => {
    setEditingDigestTargetId(null);
    setDigestTargetForm({ name: '', telegramChatId: '', telegramTopicId: '' });
    setDigestTargetModalOpen(true);
  };

  const handleScheduleTargetSelect = (value: string) => {
    if (value === '__add__') {
      setEditingDigestTargetId(null);
      setDigestTargetForm({ name: '', telegramChatId: '', telegramTopicId: '' });
      setDigestTargetModalOpen(true);
      return;
    }
    setSelectedScheduleTargetId(value);
    const selected = digestTelegramTargets.find((target) => target.id === value);
    setFormData((prev: any) => ({
      ...prev,
      scheduleTelegramChatId: selected?.telegramChatId || '',
      scheduleTelegramTopicId: selected?.telegramTopicId || ''
    }));
  };

  const handleCreateOrUpdateDigestTarget = async () => {
    const name = digestTargetForm.name.trim();
    const telegramChatId = digestTargetForm.telegramChatId.trim();
    const telegramTopicId = digestTargetForm.telegramTopicId.trim();
    if (!name || !telegramChatId) {
      alert('Заполните название и Telegram Chat ID');
      return;
    }

    try {
      setDigestTargetSaving(true);
      const payload = {
        name,
        telegramChatId,
        telegramTopicId
      };
      const saved = editingDigestTargetId
        ? await apiPut<DigestTelegramTarget>(`/digest-telegram-targets/${editingDigestTargetId}`, payload)
        : await apiPost<DigestTelegramTarget>('/digest-telegram-targets', payload);
      const normalized: DigestTelegramTarget = {
        ...saved,
        isActive: saved?.isActive !== false,
        telegramTopicId: saved?.telegramTopicId || ''
      };
      setDigestTelegramTargets((prev) => {
        const withoutSame = prev.filter((item) => item.id !== normalized.id);
        return [...withoutSame, normalized].sort((a, b) => a.name.localeCompare(b.name));
      });

      if (!editingDigestTargetId || selectedDigestTargetId === editingDigestTargetId) {
        setSelectedDigestTargetId(normalized.id);
      }
      if (editingDigestTargetId && selectedResultsTargetIds.includes(editingDigestTargetId)) {
        setSelectedResultsTargetIds((prev) => prev.map((id) => (id === editingDigestTargetId ? normalized.id : id)));
      }
      if (selectedScheduleTargetId === editingDigestTargetId) {
        setSelectedScheduleTargetId(normalized.id);
      }

      setFormData((prev: any) => ({
        ...prev,
        digestTelegramChatId:
          !editingDigestTargetId || selectedDigestTargetId === editingDigestTargetId
            ? normalized.telegramChatId
            : prev.digestTelegramChatId,
        digestTelegramTopicId:
          !editingDigestTargetId || selectedDigestTargetId === editingDigestTargetId
            ? normalized.telegramTopicId
            : prev.digestTelegramTopicId,
        resultsTelegramChatId:
          editingDigestTargetId && selectedResultsTargetIds.includes(editingDigestTargetId)
            ? normalized.telegramChatId
            : prev.resultsTelegramChatId,
        resultsTelegramTopicId:
          editingDigestTargetId && selectedResultsTargetIds.includes(editingDigestTargetId)
            ? normalized.telegramTopicId
            : prev.resultsTelegramTopicId,
        scheduleTelegramChatId:
          selectedScheduleTargetId === editingDigestTargetId
            ? normalized.telegramChatId
            : prev.scheduleTelegramChatId,
        scheduleTelegramTopicId:
          selectedScheduleTargetId === editingDigestTargetId
            ? normalized.telegramTopicId
            : prev.scheduleTelegramTopicId
      }));
      setDigestTargetModalOpen(false);
      setEditingDigestTargetId(null);
      setDigestTargetForm({ name: '', telegramChatId: '', telegramTopicId: '' });
    } catch (err: any) {
      console.error('Error saving digest target:', err);
      alert(`Ошибка сохранения записи: ${err?.message || 'Неизвестная ошибка'}`);
    } finally {
      setDigestTargetSaving(false);
    }
  };

  const openEditDigestTarget = (targetId: string) => {
    const selected = digestTelegramTargets.find((target) => target.id === targetId);
    if (!selected) {
      alert('Сначала выберите элемент из списка');
      return;
    }
    setEditingDigestTargetId(selected.id);
    setDigestTargetForm({
      name: selected.name || '',
      telegramChatId: selected.telegramChatId || '',
      telegramTopicId: selected.telegramTopicId || ''
    });
    setDigestTargetModalOpen(true);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
      <div className="bg-white rounded-lg w-full max-w-5xl mx-4 my-4 flex flex-col max-h-[90vh]">
        <div className="px-4 py-3 border-b border-gray-200 flex-shrink-0">
          <h2 className="text-lg font-bold">
            {tournament ? 'Редактировать турнир' : 'Создать турнир'}
          </h2>
        </div>

        <form id="tournament-form" onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Название *
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Цвет турнира
            </label>
            <input
              type="color"
              name="color"
              value={(formData as any).color}
              onChange={handleChange}
              className="w-16 h-10 border border-gray-300 rounded-lg px-1 py-1"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Описание
            </label>
            <input
              type="text"
              name="description"
              value={formData.description}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Дата начала *
              </label>
              <input
                type="date"
                name="startDate"
                value={formData.startDate}
                onChange={handleChange}
                required
                className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Дата окончания
              </label>
              <input
                type="date"
                name="endDate"
                value={formData.endDate}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Сезон
              </label>
              <select
                name="season"
                value={(formData as any).season || ''}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Выберите сезон</option>
                {SEASON_OPTIONS.map((season) => (
                  <option key={season} value={season}>
                    {season}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Регистрация с
              </label>
              <input
                type="datetime-local"
                name="registrationStartAt"
                value={(formData as any).registrationStartAt || ''}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Регистрация по
              </label>
              <input
                type="datetime-local"
                name="registrationEndAt"
                value={(formData as any).registrationEndAt || ''}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          <p className="text-xs text-gray-500 -mt-2">
            Время регистрации указывается в часовом поясе Europe/Chisinau.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Статус
              </label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="PLANNED">Запланирован</option>
                <option value="ACTIVE">Активный</option>
                <option value="COMPLETED">Завершен</option>
                <option value="CANCELLED">Отменен</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Формат турнира *
              </label>
              <select
                name="format"
                value={formData.format}
                onChange={handleChange}
                required
                className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="SINGLES">Одиночный</option>
                <option value="DOUBLES">Парный</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Формат сетки
              </label>
              <select
                name="defaultBracketFormat"
                value={((formData as any).defaultBracketFormat || '').toString()}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Не выбрано</option>
                <option value="SINGLE_ELIMINATION">Elimination</option>
                <option value="ROUND_ROBIN">Round Robin</option>
                <option value="MIXED">Mixed</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-3">
              <div>
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    name="hideResults"
                    checked={(formData as any).hideResults || false}
                    onChange={(e) => setFormData({ ...formData, hideResults: e.target.checked })}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    Не отображать результаты
                  </span>
                </label>
                <p className="text-xs text-gray-500 mt-0.5 ml-6">
                  Если отмечено, турнир не будет отображаться в списке результатов в Telegram боте
                </p>
              </div>

              <div>
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    name="excludeFromWebsite"
                    checked={(formData as any).excludeFromWebsite || false}
                    onChange={(e) => setFormData({ ...formData, excludeFromWebsite: e.target.checked })}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    Не передавать на сайт
                  </span>
                </label>
                <p className="text-xs text-gray-500 mt-0.5 ml-6">
                  Если отмечено, турнир и его матчи не будут отдаваться в публичный API сайта
                </p>
              </div>
            </div>

            <div>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  name="disableFeeAccrual"
                  checked={(formData as any).disableFeeAccrual || false}
                  onChange={(e) => setFormData({ ...formData, disableFeeAccrual: e.target.checked })}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-gray-700">
                  Не начислять взносы
                </span>
              </label>
              <p className="text-xs text-gray-500 mt-0.5 ml-6 mb-3">
                Если отмечено, задолженности по взносам за этот турнир не создаются
              </p>

              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  name="showCourts"
                  checked={(formData as any).showCourts || false}
                  onChange={(e) => setFormData({ ...formData, showCourts: e.target.checked })}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-gray-700">
                  Отображать Доступные корты
                </span>
              </label>
              <p className="text-xs text-gray-500 mt-0.5 ml-6">
                Если отмечено, турнир будет отображаться в списке доступных кортов в Telegram боте
              </p>

              <label className="flex items-center space-x-2 mt-3">
                <input
                  type="checkbox"
                  name="inviteOnlyRegistration"
                  checked={(formData as any).inviteOnlyRegistration || false}
                  onChange={(e) => setFormData({ ...formData, inviteOnlyRegistration: e.target.checked })}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-gray-700">
                  Регистрация по приглашениям
                </span>
              </label>
              <p className="text-xs text-gray-500 mt-0.5 ml-6">
                Если отмечено, self-registration через TTM.md будет заблокирован (только приглашения/ручная регистрация)
              </p>
            </div>
          </div>

          <div className="border border-emerald-200 rounded-lg p-3 space-y-3 bg-emerald-50/30">
            <h3 className="text-sm font-semibold text-gray-800">Публичный контент</h3>
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                name="rulesIsPublished"
                checked={(formData as any).rulesIsPublished || false}
                onChange={(e) => setFormData({ ...formData, rulesIsPublished: e.target.checked } as any)}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="text-sm font-medium text-gray-700">
                Опубликовать регламент
              </span>
            </label>

            {(formData as any).rulesUpdatedAt && (
              <p className="text-xs text-gray-500">
                Обновлено: {new Date((formData as any).rulesUpdatedAt).toLocaleString('ru-RU')}
              </p>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Регламент (RU)
              </label>
              <textarea
                name="rulesRu"
                value={(formData as any).rulesRu || ''}
                onChange={handleChange}
                rows={6}
                className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Текст регламента на русском"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Regulament (RO)
              </label>
              <textarea
                name="rulesRo"
                value={(formData as any).rulesRo || ''}
                onChange={handleChange}
                rows={6}
                className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Textul regulamentului in romana"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Rules (EN)
              </label>
              <textarea
                name="rulesEn"
                value={(formData as any).rulesEn || ''
                }
                onChange={handleChange}
                rows={6}
                className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Tournament rules in English"
              />
            </div>
          </div>

          <div className="border border-blue-200 rounded-lg p-3 space-y-3 bg-blue-50/30">
            <h3 className="text-sm font-semibold text-gray-800">Digest / Telegram updates</h3>

            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                name="digestEnabled"
                checked={(formData as any).digestEnabled || false}
                onChange={(e) => setFormData({ ...formData, digestEnabled: e.target.checked })}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="text-sm font-medium text-gray-700">
                Автосводка регистрации включена
              </span>
            </label>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Время отправки
                </label>
                <input
                  type="time"
                  name="digestSendTime"
                  value={(formData as any).digestSendTime || ''}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Таймзона
                </label>
                <input
                  type="text"
                  name="digestTimeZone"
                  value={(formData as any).digestTimeZone || ''}
                  onChange={handleChange}
                  placeholder="Europe/Chisinau"
                  className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Группа / топик для digest
                </label>
                <select
                  value={selectedDigestTargetId || ''}
                  onChange={(e) => handleDigestTargetSelect(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="__add__">+ Добавить</option>
                  <option value="">Не выбрано</option>
                  {digestTelegramTargets.map((target) => (
                    <option key={target.id} value={target.id}>
                      {target.name} ({target.telegramChatId} / {target.telegramTopicId || 'General'})
                    </option>
                  ))}
                </select>
                <div className="mt-1">
                  <button
                    type="button"
                    onClick={() => openEditDigestTarget(selectedDigestTargetId)}
                    disabled={!selectedDigestTargetId}
                    className="text-xs text-blue-700 hover:text-blue-900 disabled:text-gray-400"
                  >
                    Редактировать выбранный элемент
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Язык публикации
                </label>
                <select
                  name="digestLanguageMode"
                  value={(formData as any).digestLanguageMode || 'ro_ru'}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="ro_ru">ro_ru</option>
                  <option value="ru_ro">ru_ro</option>
                  <option value="ro">ro</option>
                  <option value="ru">ru</option>
                  <option value="en">en</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Ссылка на список участников
                </label>
                <input
                  type="text"
                  name="digestParticipantsListLink"
                  value={(formData as any).digestParticipantsListLink || ''}
                  onChange={handleChange}
                  placeholder="https://ttm.md/..."
                  className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Ссылка на регистрацию
                </label>
                <input
                  type="text"
                  name="digestRegistrationLink"
                  value={(formData as any).digestRegistrationLink || ''}
                  onChange={handleChange}
                  placeholder="https://ttm.bogslevel.com/..."
                  className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="border-t border-blue-200 pt-3 space-y-2">
              <p className="text-xs font-medium text-gray-700">Ручная отправка digest</p>

              <div className="grid grid-cols-1 md:grid-cols-[255px_1fr] gap-2 items-center">
                <button
                  type="button"
                  onClick={() => handleSendManualDigest('initial')}
                  disabled={digestSendingMode !== null || !tournament?.id}
                  className="px-3 py-1.5 text-sm bg-gray-500 text-white rounded-lg hover:bg-gray-600 disabled:opacity-50"
                >
                  {digestSendingMode === 'initial' ? 'Отправка...' : 'Отправить начальный'}
                </button>
                <select
                  value={manualDigestTemplates.initial}
                  onChange={(e) => handleManualDigestTemplateChange('initial', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Шаблон турнира / default</option>
                  {digestTemplates.map((template) => (
                    <option key={template.id} value={template.id}>
                      {template.name} ({template.languageMode}){template.isDefault ? ' [default]' : ''}
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-[255px_1fr] gap-2 items-center">
                <button
                  type="button"
                  onClick={() => handleSendManualDigest('current')}
                  disabled={digestSendingMode !== null || !tournament?.id}
                  className="px-3 py-1.5 text-sm bg-gray-500 text-white rounded-lg hover:bg-gray-600 disabled:opacity-50"
                >
                  {digestSendingMode === 'current' ? 'Отправка...' : 'Отправить текущий'}
                </button>
                <select
                  value={(formData as any).digestTemplateId || ''}
                  onChange={(e) => handleManualDigestTemplateChange('current', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Шаблон турнира / default</option>
                  {digestTemplates.map((template) => (
                    <option key={template.id} value={template.id}>
                      {template.name} ({template.languageMode}){template.isDefault ? ' [default]' : ''}
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-[255px_1fr] gap-2 items-center">
                <button
                  type="button"
                  onClick={() => handleSendManualDigest('final')}
                  disabled={digestSendingMode !== null || !tournament?.id}
                  className="px-3 py-1.5 text-sm bg-gray-500 text-white rounded-lg hover:bg-gray-600 disabled:opacity-50"
                >
                  {digestSendingMode === 'final' ? 'Отправка...' : 'Отправить итоговый'}
                </button>
                <select
                  value={manualDigestTemplates.final}
                  onChange={(e) => handleManualDigestTemplateChange('final', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Шаблон турнира / default</option>
                  {digestTemplates.map((template) => (
                    <option key={template.id} value={template.id}>
                      {template.name} ({template.languageMode}){template.isDefault ? ' [default]' : ''}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="border border-blue-200 rounded-lg p-3 space-y-3 bg-blue-50/30">
            <h3 className="text-sm font-semibold text-gray-800">Telegram results</h3>

            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                name="resultsTelegramEnabled"
                checked={(formData as any).resultsTelegramEnabled || false}
                onChange={(e) => setFormData({ ...formData, resultsTelegramEnabled: e.target.checked })}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="text-sm font-medium text-gray-700">
                Автопубликация результатов матчей
              </span>
            </label>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Группы / топики для результатов
              </label>
              <div className="space-y-2 border border-gray-300 rounded-lg px-3 py-2 bg-white max-h-52 overflow-y-auto">
                {digestTelegramTargets.length === 0 ? (
                  <p className="text-xs text-gray-500">Сначала добавьте хотя бы одну комбинацию Группа / топик.</p>
                ) : (
                  digestTelegramTargets.map((target) => {
                    const checked = selectedResultsTargetIds.includes(target.id);
                    return (
                      <label key={target.id} className="flex items-center justify-between gap-3 text-sm text-gray-700">
                        <span className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={(e) => handleResultsTargetToggle(target.id, e.target.checked)}
                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                          />
                          <span>{target.name} ({target.telegramChatId} / {target.telegramTopicId || 'General'})</span>
                        </span>
                        <button
                          type="button"
                          onClick={() => openEditDigestTarget(target.id)}
                          className="text-xs text-blue-700 hover:text-blue-900"
                        >
                          Редактировать
                        </button>
                      </label>
                    );
                  })
                )}
              </div>
              <div className="mt-2">
                <button
                  type="button"
                  onClick={handleAddResultsTarget}
                  className="text-xs text-blue-700 hover:text-blue-900"
                >
                  + Добавить комбинацию Группа / топик
                </button>
              </div>
            </div>
          </div>

          <div className="border border-emerald-200 rounded-lg p-3 space-y-3 bg-emerald-50/30">
            <h3 className="text-sm font-semibold text-gray-800">Telegram schedule</h3>

            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                name="scheduleTelegramEnabled"
                checked={(formData as any).scheduleTelegramEnabled || false}
                onChange={(e) => setFormData({ ...formData, scheduleTelegramEnabled: e.target.checked })}
                className="w-4 h-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
              />
              <span className="text-sm font-medium text-gray-700">
                Автопубликация назначений матчей
              </span>
            </label>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Группа / топик для назначений
              </label>
              <select
                value={selectedScheduleTargetId || ''}
                onChange={(e) => handleScheduleTargetSelect(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              >
                <option value="__add__">+ Добавить</option>
                <option value="">Не выбрано</option>
                {digestTelegramTargets.map((target) => (
                  <option key={target.id} value={target.id}>
                    {target.name} ({target.telegramChatId} / {target.telegramTopicId || 'General'})
                  </option>
                ))}
              </select>
              <div className="mt-1">
                <button
                  type="button"
                  onClick={() => openEditDigestTarget(selectedScheduleTargetId)}
                  disabled={!selectedScheduleTargetId}
                  className="text-xs text-emerald-700 hover:text-emerald-900 disabled:text-gray-400"
                >
                  Редактировать выбранный элемент
                </button>
              </div>
            </div>
          </div>

          {/* Логотипы для подписи */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Логотипы для подписи
            </label>
            <p className="text-xs text-gray-500 mb-2">
              Выберите файлы логотипов из файловой системы. Логотипы будут отображаться в таблице результатов.
            </p>
            <div className="space-y-2">
              {((formData as any).logos || []).map((logo: string, index: number) => (
                <div key={index} className="flex items-center gap-2">
                  <div className="flex-1 border border-gray-300 rounded-lg px-2 py-1.5 bg-gray-50 text-gray-700 text-sm">
                    {logo || `Логотип ${index + 1} не выбран`}
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setFilePickerIndex(index);
                      setFilePickerOpen(true);
                    }}
                    className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    {logo ? 'Изменить' : 'Выбрать'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const newLogos = ((formData as any).logos || []).filter((_: string, i: number) => i !== index);
                      setFormData({ ...formData, logos: newLogos } as any);
                    }}
                    className="px-2 py-1.5 text-sm text-red-600 hover:text-red-800"
                  >
                    Удалить
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => {
                  const newLogos = [...((formData as any).logos || []), ''];
                  setFormData({ ...formData, logos: newLogos } as any);
                }}
                className="px-3 py-1.5 text-sm text-blue-600 hover:text-blue-800 border border-blue-300 rounded-lg"
              >
                + Добавить логотип
              </button>
            </div>
          </div>

          {/* Удалено поле Бюджет по требованию */}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Клубы
              </label>
              <div className="max-h-48 overflow-y-auto border border-gray-300 rounded-lg p-1.5 space-y-1">
                <div className="grid grid-cols-[1fr_110px_140px] gap-2 px-1.5 py-1 text-[11px] font-medium text-gray-500 uppercase tracking-wide">
                  <span>Клуб</span>
                  <span>Цена</span>
                  <span>Покрытие</span>
                </div>
                {clubs.map((club) => (
                  <div key={club.id} className="grid grid-cols-[1fr_110px_140px] gap-2 items-center py-0.5 hover:bg-gray-50 rounded px-1.5">
                    <label className="flex items-center space-x-2 min-w-0">
                      <input
                        type="checkbox"
                        checked={formData.clubIds.includes(club.id)}
                        onChange={() => handleClubToggle(club.id)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700 truncate">
                        {club.name} {club.address && `(${club.address})`}
                      </span>
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="MDL/час"
                      disabled={!formData.clubIds.includes(club.id)}
                      value={formData.clubPrices[club.id] || ''}
                      onChange={(e) => handleClubPriceChange(club.id, e.target.value)}
                      className="w-full border border-gray-300 rounded px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-400"
                    />
                    <select
                      disabled={!formData.clubIds.includes(club.id)}
                      value={((formData as any).clubSurfaces?.[club.id] || '').toString()}
                      onChange={(e) => handleClubSurfaceChange(club.id, e.target.value)}
                      className="w-full border border-gray-300 rounded px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-400"
                    >
                      <option value="">Не выбрано</option>
                      {SURFACE_OPTIONS.map((surface) => (
                        <option key={surface.value} value={surface.value}>
                          {surface.label}
                        </option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-0.5">
                Выберите один или несколько клубов для проведения турнира. Для каждого клуба можно задать цену и покрытие.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Уровни для регистрации
              </label>
              <div className="grid grid-cols-1 lg:grid-cols-[1fr_180px] gap-3 items-start">
                <div className="max-h-48 overflow-y-auto border border-gray-300 rounded-lg p-1.5 space-y-1">
                  {TOURNAMENT_LEVELS.map((level) => {
                    const cfg = (formData as any).registrationLevels?.[level] || { enabled: false, maxPlayers: '' };
                    return (
                      <div key={level} className="flex items-center space-x-2 py-0.5 hover:bg-gray-50 rounded px-1.5">
                        <input
                          type="checkbox"
                          checked={cfg.enabled}
                          onChange={() => handleRegistrationLevelToggle(level)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700 flex-1">{level}</span>
                        {cfg.enabled && (
                          <div className="flex items-center space-x-1">
                            <span className="text-xs text-gray-500">Макс.:</span>
                            <input
                              type="number"
                              min="1"
                              step="1"
                              placeholder="игроков"
                              value={cfg.maxPlayers}
                              onChange={(e) => handleRegistrationLevelMaxPlayersChange(level, e.target.value)}
                              className="w-20 border border-gray-300 rounded px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                <div className="border border-gray-300 rounded-lg p-2.5 bg-gray-50">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Базовый взнос
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    name="baseFee"
                    value={(formData as any).baseFee ?? ''}
                    onChange={handleChange}
                    placeholder="MDL"
                    className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Общий взнос по умолчанию
                  </p>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-0.5">
                Отметьте уровни, доступные для регистрации. Для каждого уровня можно указать максимальное количество игроков.
              </p>
            </div>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-red-800 text-sm">{error}</p>
            </div>
          )}

        </form>

        <div className="px-4 py-3 border-t border-gray-200 bg-gray-50 flex space-x-3 flex-shrink-0">
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-400 text-sm"
          >
            Отмена
          </button>
          <button
            type="submit"
            form="tournament-form"
            disabled={loading}
            className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm"
          >
            {loading ? 'Сохранение...' : 'Сохранить'}
          </button>
        </div>

        {/* Модальное окно выбора файла */}
        <FilePickerModal
          isOpen={filePickerOpen}
          onClose={() => {
            setFilePickerOpen(false);
            setFilePickerIndex(null);
          }}
          onSelect={(filePath) => {
            if (filePickerIndex !== null) {
              const newLogos = [...((formData as any).logos || [])];
              newLogos[filePickerIndex] = filePath;
              setFormData({ ...formData, logos: newLogos } as any);
            }
            setFilePickerOpen(false);
            setFilePickerIndex(null);
          }}
          allowedExtensions={['.png', '.jpg', '.jpeg', '.webp', '.svg', '.gif']}
        />

        {digestTargetModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-[60]">
            <div className="bg-white rounded-lg w-full max-w-md mx-4 p-4 space-y-3">
              <h3 className="text-base font-semibold text-gray-800">
                {editingDigestTargetId ? 'Редактировать комбинацию Группа / топик' : 'Новая комбинация Группа / топик'}
              </h3>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Название</label>
                <input
                  type="text"
                  value={digestTargetForm.name}
                  onChange={(e) => setDigestTargetForm((prev) => ({ ...prev, name: e.target.value }))}
                  placeholder="Digest турнира / Основной топик"
                  className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Telegram Chat ID</label>
                <input
                  type="text"
                  value={digestTargetForm.telegramChatId}
                  onChange={(e) => setDigestTargetForm((prev) => ({ ...prev, telegramChatId: e.target.value }))}
                  placeholder="-1001234567890"
                  className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Telegram Topic ID (thread) — необязательно</label>
                <input
                  type="text"
                  value={digestTargetForm.telegramTopicId}
                  onChange={(e) => setDigestTargetForm((prev) => ({ ...prev, telegramTopicId: e.target.value }))}
                  placeholder="12345 (или оставить пустым для General)"
                  className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="flex justify-end space-x-2 pt-1">
                <button
                  type="button"
                  onClick={() => {
                    if (!digestTargetSaving) {
                      setDigestTargetModalOpen(false);
                      setEditingDigestTargetId(null);
                      setDigestTargetForm({ name: '', telegramChatId: '', telegramTopicId: '' });
                    }
                  }}
                  className="px-3 py-1.5 text-sm bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                  disabled={digestTargetSaving}
                >
                  Отмена
                </button>
                <button
                  type="button"
                  onClick={handleCreateOrUpdateDigestTarget}
                  className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                  disabled={digestTargetSaving}
                >
                  {digestTargetSaving ? 'Сохранение...' : (editingDigestTargetId ? 'Сохранить изменения' : 'Сохранить')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TournamentForm;
